import { Colors, DropdownInput, Option, Button, Tooltip } from "@snapchat/snap-design-system";
import { CheckCircleIcon } from "@snapchat/snap-design-system-icons";

import React, { type FC, useState, useCallback, useEffect } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import { AbsoluteRoute } from "../../../core/routes";
import { DropdownWithCreateNew } from "../../common/components/DropdownWithCreateNew";
import { DecreaseText } from "../../common/components/text";
import { Flexbox } from "../../core/components";
import { useModal, useSearchParams } from "../../core/hooks";
import { useOrganizations, useSwitchOrganization } from "../../organizations/hooks";
import { useOnboardingOrganization } from "../hooks/useOnboardingOrganizations";
import { NewOrganizationModal } from "./NewOrganizationModal";
import { ContainerWithCamkitIcon } from "../../common/containers/onboarding/ContainerWithCamkitIcon";
import { Title, dropdownStyle, ErrorMessage, flexOptionstyles } from "./OrganizationSelection.styles";

export const OrganizationSelection: FC = () => {
    const { organizations: allOrganizations } = useOrganizations();
    const { organizations, loading, enableOrg, isEnabling, fetchAllOrgs, error } = useOnboardingOrganization();
    const { selectedOrganizationId: globalSelectedOrganizationId, switchOrganization } = useSwitchOrganization();
    const { token } = useSearchParams();
    const [selectedOrganization, setSelectedOrganization] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [modalProps, showModal] = useModal();
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const navigate = useNavigate();

    const submitOrganization = async () => {
        try {
            const { errors } = await enableOrg({
                variables: {
                    orgId: selectedOrganization,
                    token: token ?? "",
                },
            });

            if (errors) throw new Error(errors[0]?.message);
        } catch (e) {
            throw e;
        }
    };

    const getIsOrgEnabled = useCallback(
        (orgId: string) => {
            return Boolean(allOrganizations.filter((org) => org?.id === orgId).length);
        },
        [allOrganizations],
    );

    const selectClicked = async () => {
        setHasError(false);
        setIsSubmitting(true);

        try {
            await submitOrganization();
            setHasSubmitted(true);
        } catch (e) {
            setHasError(true);
            setIsSubmitting(false);
            console.error(e);
        }
    };

    const handleNewOrganization = (id: string) => {
        setSelectedOrganization(id);
        modalProps.onCancel();
    };

    const handleSwitchAndRedirect = useCallback(() => {
        navigate(generatePath(AbsoluteRoute.ORG_APPS_CREATE, { orgId: selectedOrganization ?? "" }));
        switchOrganization(organizations.find((org) => org?.id === selectedOrganization));
    }, [organizations, selectedOrganization, switchOrganization]);

    useEffect(() => {
        if (hasSubmitted) {
            handleSwitchAndRedirect();
        }
    }, [organizations, hasSubmitted, handleSwitchAndRedirect]);

    useEffect(() => {
        // Fetch all orgs on mount
        fetchAllOrgs();
    }, [fetchAllOrgs]);

    return (
        <ContainerWithCamkitIcon>
            <Title>Get Started With Camera Kit</Title>
            <p>Select an Organization to get started!</p>
            <DropdownInput
                disabled={isSubmitting}
                className={dropdownStyle}
                loading={loading && error == null}
                error={error != null && <p>{error?.message ?? "An error occured while loading organizations"}</p>}
                placeholder="Choose an organization..."
                value={selectedOrganization}
                onChange={setSelectedOrganization}
                dropdownRender={(menuNode: React.ReactNode) => (
                    <DropdownWithCreateNew menuNode={menuNode} text="Create Organization" showModal={showModal} />
                )}
                data-testid="organization-dropdown"
            >
                {organizations.map((org) => (
                    <Option className={flexOptionstyles} value={org?.id} key={org?.id}>
                        <DecreaseText width="95%">
                            {org?.id === globalSelectedOrganizationId ? `${org?.name} (Current)` : org?.name}
                        </DecreaseText>
                        {getIsOrgEnabled(org?.id ?? "") && (
                            <Tooltip content="Camera Kit is enabled for this organization.">
                                <CheckCircleIcon fill={Colors.Green.V150} />
                            </Tooltip>
                        )}
                    </Option>
                ))}
            </DropdownInput>
            <Flexbox>
                <Button
                    onClick={selectClicked}
                    type="primary"
                    isPending={isSubmitting || isEnabling}
                    isDisabled={!selectedOrganization}
                    data-testid="organization-submit"
                >
                    Continue
                </Button>
            </Flexbox>
            {hasError && <ErrorMessage>There was an error submitting your request. Please try again.</ErrorMessage>}
            <NewOrganizationModal {...modalProps} handleNewOrganization={handleNewOrganization} />
        </ContainerWithCamkitIcon>
    );
};
